import { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Upload,
    Popconfirm,
    message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import SlidesApi from '../api/slides';
import LangApi from '../api/langs';

const Slides = () => {
    const [slides, setSlides] = useState([]);
    const [langs, setLangs] = useState([]);
    const [selectedLang, setSelectedLang] = useState('ru');
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [modalVisible, setModalVisible] = useState(false);
    const [editing, setEditing] = useState(null);
    const [fileList, setFileList] = useState([]);
    const BASE_URL = process.env.REACT_APP_URL;

    const fetchLangs = async () => {
        const res = await LangApi.getAll();
        setLangs(res.data);
    };

    const fetchSlides = async () => {
        setLoading(true);
        try {
            const res = await SlidesApi.getByLocale(selectedLang);
            setSlides(res.data);
        } catch {
            message.error('Не удалось загрузить слайды');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLangs();
    }, []);

    useEffect(() => {
        fetchSlides();
    }, [selectedLang]);

    const openModal = (record = null) => {
        setEditing(record);
        setModalVisible(true);
        form.resetFields();
        if (record) {
            form.setFieldsValue(record);
        }
    };

    const closeModal = () => {
        setModalVisible(false);
        form.resetFields();
        setEditing(null);
        setFileList([]);
    };

    const handleSubmit = async (values) => {
        try {
            if (editing) {
                await SlidesApi.update(editing.id, values);
                message.success('Слайд обновлён');
            } else {
                const imageFile = fileList?.[0]?.originFileObj;

                if (!imageFile) {
                    return message.error('Изображение обязательно');
                }

                const formData = new FormData();
                formData.append('title', values.title);
                formData.append('description', values.description);
                formData.append('lang_id', values.lang_id);
                formData.append('image', imageFile);

                await SlidesApi.create(formData);
                message.success('Слайд добавлен');
            }

            closeModal();
            fetchSlides();
        } catch {
            message.error('Ошибка при сохранении');
        }
    };

    const handleDelete = async (id) => {
        await SlidesApi.remove(id);
        fetchSlides();
        message.success('Удалено');
    };

    const columns = [
        {
            title: 'Изображение',
            dataIndex: 'image',
            render: (img) => img && <img src={BASE_URL+img} className="w-20 rounded" />,
        },
        { title: 'Заголовок', dataIndex: 'title' },
        { title: 'Описание', dataIndex: 'description' },
        {
            title: 'Действия',
            render: (_, record) => (
                <div className="flex gap-2">
                    <Button onClick={() => openModal(record)} size="small">Редактировать</Button>
                    <Popconfirm title="Удалить?" onConfirm={() => handleDelete(record.id)}>
                        <Button danger size="small">Удалить</Button>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Слайды</h2>
                <div className="flex gap-2">
                    <Select
                        value={selectedLang}
                        onChange={setSelectedLang}
                        options={langs.map((l) => ({ label: l.name, value: l.code }))}
                        style={{ width: 160 }}
                    />
                    <Button type="primary" onClick={() => openModal()}>Добавить</Button>
                </div>
            </div>

            <Table
                dataSource={slides}
                columns={columns}
                rowKey="id"
                loading={loading}
            />

            <Modal
                title={editing ? 'Редактировать слайд' : 'Добавить слайд'}
                open={modalVisible}
                onCancel={closeModal}
                onOk={() => form.submit()}
                okText="Сохранить"
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {!editing && (
                        <Form.Item name="lang_id" label="Язык" rules={[{ required: true }]}>
                            <Select options={langs.map((l) => ({ label: l.name, value: l.id }))} />
                        </Form.Item>
                    )}
                    <Form.Item name="title" label="Заголовок" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="description" label="Описание" rules={[{ required: true }]}>
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    {!editing && (
                        <Form.Item label="Изображение" required>
                            <Upload
                                beforeUpload={() => false}
                                listType="picture"
                                maxCount={1}
                                fileList={fileList}
                                onChange={({ fileList }) => setFileList(fileList)}
                                accept="image/*"
                            >
                                <Button icon={<UploadOutlined />}>Загрузить изображение</Button>
                            </Upload>
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default Slides;
